import { QueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";

import type { Frame } from "@/types/dragon-tiles";

import {
  findHydratedConnectorTileById,
  hydratedConnectorsQueryOptions,
  type HydratedConnector,
} from "@/api/connectors/index";
import { frameQueryOptions } from "@/api/dragon-tiles";
import FrameManager from "@/features/FrameManager/FrameManager";

type FrameRouteLoaderData = {
  frame: Frame;
  siteId: string;
  connectorId: string;
  dragonTileId: string;
};

export const Route = createFileRoute(
  "/_auth/dragon-tiles/$siteId/connectors/$parentTile/tile/$frameId",
)({
  component: RouteComponent,
  loader: async ({ params, context }): Promise<FrameRouteLoaderData> => {
    const { queryClient } = context as { queryClient: QueryClient };
    const { siteId, parentTile: connectorId, frameId } = params;

    const hydratedConnectors = await queryClient.ensureQueryData<
      HydratedConnector[]
    >(hydratedConnectorsQueryOptions(siteId, queryClient));
    const hydrated = findHydratedConnectorTileById(
      hydratedConnectors,
      connectorId,
    );
    if (!hydrated) {
      throw new Error("Connector tile not found");
    }

    const frame = await queryClient.fetchQuery(
      frameQueryOptions(siteId, frameId),
    );
    if (!frame) {
      throw new Error("Tile not found");
    }

    return {
      frame,
      siteId,
      connectorId,
      dragonTileId: hydrated.dragonTile.id,
    };
  },
});

function RouteComponent() {
  const { frame, siteId, connectorId, dragonTileId } = Route.useLoaderData();

  if (!frame || !siteId) {
    throw new Error("Required data not found");
  }

  return (
    <FrameManager
      siteId={siteId}
      parentTileId={connectorId}
      frameParentTileId={dragonTileId}
      returnTo="connector-tile"
      connectorParentTileId={connectorId}
    />
  );
}
